import React, { useEffect } from 'react';
import { Col, Row, Spinner, Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import ReactSelect, { components } from 'react-select';
import { Table, Column, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import formatters from '../../../config/formatters';
import useDashboardTables from './useDashboardTables';
import useDashboard from '../useDashboard';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { BsCaretDownFill } from 'react-icons/bs';
import { selectStyles, Theme } from '../../../config/constants';

const TopCategoriesTable = () => {
    const isLoading = useSelector(({dashboard}) => dashboard.topCategoriesIsLoading);
    const topCategoriesData = useSelector(({dashboard}) => dashboard.topCategories ?? []);
    const { onSelectChange, onClickSeeMore } = useDashboardTables.useCategoriesTable();
    const { getTopCategories } = useDashboard.useDates();
    const { usCurrencyFormatter, percentFormatter, numberFormatter } = formatters();
    const topCategoriesOptions = [
        {value: 'Total Volume', label: 'Sort By: Total Volume ($)'},
        {value: 'Category', label: 'Sort By: Category'},
        {value: 'Cases', label: 'Sort By: Cases'},
        {value: '% of Sales', label: 'Sort By: % of Sales'}
    ];

    const DropdownIndicator = props => (
        <components.DropdownIndicator {...props}>
            <BsCaretDownFill />
        </components.DropdownIndicator>
    );

    const slideDrawerContents = (
        <Row className="me-2" style={{height: '90%'}}>
            <AutoSizer>
                {({ height, width }) => (
                    <Table
                        width={width}
                        height={height}
                        headerHeight={40}
                        rowHeight={40}
                        rowCount={topCategoriesData?.length}
                        rowGetter={({ index }) => topCategoriesData?.[index]}
                        className="table topCategoriesTable border-grayLight text-size-14"
                        headerClassName="table-header"
                        rowClassName="table-row"
                    >
                        <Column label="CATEGORY" dataKey="category" width={width * 0.25} />
                        <Column 
                            label="TOTAL VOLUME ($)" 
                            dataKey="total_volume" 
                            width={width * 0.25} 
                            cellRenderer={({ cellData }) => usCurrencyFormatter.format(cellData)} 
                        />
                        <Column 
                            label="CASES" 
                            dataKey="total_cases" 
                            width={width * 0.25} 
                            cellRenderer={({ cellData }) => numberFormatter.format(cellData)} 
                        />
                        <Column 
                            label="% OF SALES" 
                            dataKey="sales_percent" 
                            width={width * 0.25} 
                            cellRenderer={({ cellData }) => percentFormatter.format(cellData)} 
                        />
                    </Table>
                )}
            </AutoSizer>
        </Row>
    );

    const initializePage = () => {
        getTopCategories();
    };

    // eslint-disable-next-line
    useEffect(initializePage, []);

    if (isLoading || topCategoriesData.length === 0) {
        return (
            <Col style={{height: '100%'}}>
                <Row className='pb-2'>
                    <Col className={(window.innerWidth < 1024 ? "col-7" : "col-5")}>
                        <ReactSelect 
                            styles={selectStyles}
                            components={{DropdownIndicator}}
                            options={topCategoriesOptions}
                            defaultValue={topCategoriesOptions[0]}
                        />
                    </Col>
                    <Col></Col>
                </Row>
                <Row style={{height: '240px'}}>
                    <div className="d-flex justify-content-center align-items-center" style={{overflowY: 'auto', height: '100%'}}>
                        {isLoading ? (
                            <Spinner color='beplBlue' animation="border" />
                        ) : (
                            <EmptyDataMessage className='m-auto' />
                        )}
                    </div>
                </Row>
            </Col>
        );
    }

    return (
        <Col style={{height: '100%'}}>
            <Row className='pb-2'>
                <Col className={(window.innerWidth < 1024 ? "col-7" : "col-5")}>
                    <ReactSelect 
                        styles={selectStyles}
                        components={{DropdownIndicator}}
                        options={topCategoriesOptions}
                        defaultValue={topCategoriesOptions[0]}
                        onChange={(e) => onSelectChange(e.value, topCategoriesData)} 
                    />
                </Col>
                <Col></Col>
                <Col className={(window.innerWidth < 1024 ? "col-4" : "")}>
                    <Button 
                        block 
                        outline={true}
                        style={{float: 'right', padding: '6px', fontSize: '14px'}} 
                        color={Theme().colors.accentName}
                        onClick={() => onClickSeeMore('All Categories', slideDrawerContents)}>See More
                    </Button>
                </Col>
            </Row>
            <Row className="me-2" style={{height: '240px'}}>
                <AutoSizer>
                    {({ height, width }) => (
                        <Table
                            width={width}
                            height={height}
                            headerHeight={40}
                            rowHeight={40}
                            rowCount={topCategoriesData?.length < 5 ? topCategoriesData?.length : 5}
                            rowGetter={({ index }) => topCategoriesData?.[index]}
                            className="table topCategoriesTable border-grayLight text-size-14"
                            headerClassName="table-header"
                            rowClassName="table-row"
                        >
                            <Column label="CATEGORY" dataKey="category" width={width * 0.25} />
                            <Column 
                                label="TOTAL VOLUME ($)" 
                                dataKey="total_volume" 
                                width={width * 0.25} 
                                cellRenderer={({ cellData }) => usCurrencyFormatter.format(cellData)} 
                            />
                            <Column 
                                label="CASES" 
                                dataKey="total_cases" 
                                width={width * 0.25} 
                                cellRenderer={({ cellData }) => numberFormatter.format(cellData)} 
                            />
                            <Column 
                                label="% OF SALES" 
                                dataKey="sales_percent" 
                                width={width * 0.25} 
                                cellRenderer={({ cellData }) => percentFormatter.format(cellData)} 
                            />
                        </Table>
                    )}
                </AutoSizer>
            </Row>
        </Col>
    );
};

export default TopCategoriesTable;